import { lazy, Suspense, useState, FunctionComponent } from 'react';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { Link } from 'dibs-elements/exports/Link';
import { FormattedMessage } from 'dibs-react-intl';
import * as tracking from 'dibs-tracking';
import styles from './PdpSharedCustomsMessage.scss';
import { PdpSharedCustomsMessage_item } from './__generated__/PdpSharedCustomsMessage_item.graphql';

type Props = {
    item: PdpSharedCustomsMessage_item;
    isClient: boolean;
};

const PdpSharedCustomsTooltip = lazy(
    () => import(/* webpackChunkName: "PdpSharedCustomsTooltip" */ './PdpSharedCustomsTooltip')
);

const PdpSharedCustomsModal = lazy(
    () => import(/* webpackChunkName: "PdpSharedCustomsModal" */ './PdpSharedCustomsModal')
);

const InfoCircleIcon = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedCustomsMessageInfoCircleIcon" */ 'dibs-icons/exports/InfoCircle'
        )
);

const InfoCircleFilledIcon = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedCustomsMessageInfoCircleFilledIcon" */ 'dibs-icons/exports/InfoCircleFilled'
        )
);

const PdpSharedCustomsMessage: FunctionComponent<Props> = ({ item, isClient }) => {
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const customsMessage = item?.customsMessaging?.message;
    const customsCode = item?.customsMessaging?.code || '';

    if (!customsMessage) {
        return null;
    }

    const gaLabel =
        customsCode === 'DUTY_EXEMPT'
            ? 'us buyer estimated customs 0'
            : 'us buyer customs may apply';

    let trailingText;
    let dutyAmountText;
    if (['DUTY_EXEMPT', 'NOT_DUTY_EXEMPT'].includes(customsCode)) {
        trailingText = (
            <FormattedMessage
                id="pdp.customs.trailingTextWithWebsite"
                defaultMessage="or view the US Customs & Border Protection {webiteLink}."
                values={{
                    webiteLink: (
                        <Link
                            href="https://www.cbp.gov/trade/basic-import-export/internet-purchases"
                            target="_blank"
                        >
                            <FormattedMessage
                                id="pdp.customs.websiteLink"
                                defaultMessage="website"
                            />
                        </Link>
                    ),
                }}
            />
        );
        dutyAmountText = (
            <>
                <br />
                <FormattedMessage
                    id="pdp.customs.dutyAmount"
                    defaultMessage="For US-bound items, the duty rate is likely to be 4-7% of the final sale price. Exceptions include items with a final sale price below $800, or antiques over 100 years old — which typically have a duty rate of 0%."
                />
                <br />
            </>
        );
    } else {
        trailingText = (
            <FormattedMessage
                id="pdp.customs.trailingTextWithoutWebsite"
                defaultMessage="or refer to your country's customs office for more details."
            />
        );
    }

    const customsDetailsText = (
        <FormattedMessage
            id="pdp.customs"
            defaultMessage="International Shipments may be subject to customs duties and taxes.
            {break}
            {dutyAmountText}
            {break}
            Buyers are responsible for any customs duties and taxes charged by your destination country in addition to the item price and shipping charges. {learnMoreLink} in our support center, {trailingText}"
            values={{
                dutyAmountText,
                break: <br />,
                learnMoreLink: (
                    <Link
                        href="https://support.1stdibs.com/s/article/Customs-Duties-and-Taxes-for-International-Shipments?_ga=2.226635358.256726246.1595260514-1431767277.1560791984"
                        target="_blank"
                    >
                        <FormattedMessage id="pdp.customs.learnMore" defaultMessage="Learn more" />
                    </Link>
                ),
                trailingText,
            }}
        />
    );

    return (
        <div className={styles.container}>
            <div className={styles.messageText}>{customsMessage}</div>
            <span className={styles.modalWrapper}>
                <Link
                    onClick={event => {
                        event.preventDefault();
                        tracking.trackEvent({
                            category: 'product',
                            action: 'view customs tooltip',
                            label: gaLabel,
                        });
                        setModalIsOpen(true);
                    }}
                >
                    <FormattedMessage
                        id="pdp.customs.learnMoreModalButton"
                        defaultMessage="Learn more"
                    />
                </Link>
                {isClient && (
                    <Suspense fallback={''}>
                        {modalIsOpen && (
                            <PdpSharedCustomsModal
                                customsDetailsText={customsDetailsText}
                                modalIsOpen={modalIsOpen}
                                setModalIsOpen={setModalIsOpen}
                            />
                        )}
                    </Suspense>
                )}
            </span>
            <span className={styles.tooltipWrapper}>
                <Link
                    className={styles.linkTooltipWrapper}
                    onClick={event => {
                        event.preventDefault();
                        if (!tooltipVisible) {
                            tracking.trackEvent({
                                category: 'product',
                                action: 'view customs tooltip',
                                label: gaLabel,
                            });
                        }
                        setTooltipVisible(!tooltipVisible);
                    }}
                >
                    {isClient && !tooltipVisible && (
                        <Suspense fallback={null}>
                            <InfoCircleIcon className={styles.tooltipIcon} />
                        </Suspense>
                    )}
                    {isClient && tooltipVisible && (
                        <Suspense fallback={null}>
                            <InfoCircleFilledIcon className={styles.tooltipIcon} />
                        </Suspense>
                    )}
                    {isClient && (
                        <Suspense fallback={null}>
                            {tooltipVisible && (
                                <PdpSharedCustomsTooltip
                                    customsDetailsText={customsDetailsText}
                                    tooltipVisible={tooltipVisible}
                                    setTooltipVisible={setTooltipVisible}
                                    isClient={isClient}
                                />
                            )}
                        </Suspense>
                    )}
                </Link>
            </span>
        </div>
    );
};

export default createFragmentContainer(PdpSharedCustomsMessage, {
    item: graphql`
        fragment PdpSharedCustomsMessage_item on Item {
            customsMessaging {
                message
                code
            }
        }
    `,
});
